import {create} from 'zustand';
import {getGPUTier, TierResult} from '@pmndrs/detect-gpu';
import {WEBGL_SUPPORTED} from "./utils/webGlSupportedStore.ts";
import {TextLayer} from "../interface/filters/layer/useLayerStore.ts";
import {ColorEffect, CrosshatchType} from "./effects/effects.ts";
import {useMemo} from "react";
import {useThree} from "@react-three/fiber";

const RENDER_SETTINGS_KEY = "RENDER_SETTINGS"

export type AntialiasOption = 'DEFAULT_HARDWARE' | 'MSAA' | 'NONE';
export type BuildingRoofRenderOption = 'FLAT' | 'FLAT_TEXTURE' | 'FLAT_3D' | '3D_ROOF'
export type TextRenderOption = 'FLAT' | 'ROTATED' | 'BENDY' | 'FLAT_UNIFORM' | 'ROTATED_UNIFORM'
export type MapTextFont = 'CINZEL' | 'EB_GARAMOND'

export type MapMeasurementUnit = 'METERS' | 'FEET';

interface GpuTierState {
  data: TierResult | null,
  isRequesting: boolean,
  setIsRequesting: () => void,
  setData: (data: TierResult) => void
}

const useGpuTierStore = create<GpuTierState>()(set => ({
  data: null,
  isRequesting: false,
  setIsRequesting: () => set({isRequesting: true}),
  setData: data => set({data, isRequesting: false})
}));

export interface EffectsState {
  colorEffect: ColorEffect,
  pixelation: number,
  vignette: boolean,
  dotScreen: boolean,
  scanline: boolean,
  crosshatch: CrosshatchType,
  crosshatchNoise: boolean,
  crosshatchParchment: boolean,
}

export interface RenderState extends EffectsState {
  antialiasing: AntialiasOption,
  shadows: boolean,
  shadowResolution: number,
  fieldFade: boolean,
  waterEffects: boolean,
  textures: boolean,
  dayNightCycle: boolean,
  buildingRoofRenderOption: BuildingRoofRenderOption,
  materialMultipleColours: boolean,
  districtText: TextRenderOption,
  districtTextScaling: boolean,
  customTextScale: number,
  treePercentage: number,
  threeDimensional: boolean,
  mapTextFont: MapTextFont,
  defaultTextLayer: TextLayer,
  mapMeasurementUnit: MapMeasurementUnit,
  svgRenderer: boolean,
  stats: boolean,
  ready: boolean,
}

interface RenderOptionsState {
  state: Partial<RenderState>,
  forceRefresh: boolean,
  setOptions: (renderState: Partial<RenderState>) => void,
  enableSvgRenderer: () => void,
  confirmRefresh: () => void,
  initiateRefresh: () => void,
  reset: () => void
}

function getLocalStorageRenderState(): Partial<RenderState> {
  return JSON.parse(window.localStorage.getItem(RENDER_SETTINGS_KEY) ?? '{}') as Partial<RenderState>
}

export const useRenderOptionsStore = create<RenderOptionsState>()((set, get) => ({
  state: getLocalStorageRenderState(),
  forceRefresh: false,
  setOptions: (state) => {
    const newState = { ...get().state, ...state }
    window.localStorage.setItem(RENDER_SETTINGS_KEY, JSON.stringify(newState));
    set({ state: newState })
  },
  enableSvgRenderer: () => set({state: { svgRenderer: true }}),
  confirmRefresh: () => set({forceRefresh: false}),
  initiateRefresh: () => set({forceRefresh: true}),
  reset: () => {
    window.localStorage.setItem(RENDER_SETTINGS_KEY, '{}')
    set({state: {}})
  }
}));

export function useRenderOptions(): RenderState;
export function useRenderOptions<T>(selector: (state: RenderState) => T): T;
export function useRenderOptions<T>(selector?: (state: RenderState) => T): T | RenderState {
  const { state } = useRenderOptionsStore();
  const {data, isRequesting, setIsRequesting, setData} = useGpuTierStore();

  const needToLoadGPUTier = [
    state.antialiasing,
    state.shadows,
    state.buildingRoofRenderOption,
    state.treePercentage,
    state.svgRenderer,
    state.shadowResolution,
  ].includes(undefined)

  if (needToLoadGPUTier && !data && !isRequesting) {
    setIsRequesting();
    getGPUTier().then(result => {
      setData(result);
    });
  }

  const svgRendererVal = !WEBGL_SUPPORTED || (state.svgRenderer ?? false)

  const renderState: RenderState = useMemo(() => {
    return ({
      antialiasing: state.antialiasing || appleOrIntelPerformance(data, 'DEFAULT_HARDWARE', 'MSAA'),
      shadows: state.shadows ?? true,
      shadowResolution: state.shadowResolution ?? mobileOrLowPerformance(data, 2048, 8192),
      districtText: state.districtText ?? 'BENDY',
      districtTextScaling: state.districtTextScaling ?? true,
      dayNightCycle: state.dayNightCycle ?? true,
      fieldFade: state.fieldFade ?? true,
      waterEffects: state.waterEffects ?? true,
      mapTextFont: state.mapTextFont ?? 'EB_GARAMOND',
      mapMeasurementUnit: state.mapMeasurementUnit ?? 'METERS',
      customTextScale: state.customTextScale ?? 0.8,
      buildingRoofRenderOption: state.buildingRoofRenderOption ?? mobileOrLowPerformance<BuildingRoofRenderOption>(data, 'FLAT_TEXTURE', 'FLAT_3D'),
      materialMultipleColours: state.materialMultipleColours ?? true,
      treePercentage: state.treePercentage ?? mobileOrLowPerformance(data, 50, 100),
      defaultTextLayer: state.defaultTextLayer ?? 'DISTRICTS',
      threeDimensional: !svgRendererVal && (state.threeDimensional ?? false),
      textures: state.textures ?? true,
      stats: state.stats ?? false,
      svgRenderer: svgRendererVal,
      ready: !needToLoadGPUTier || !!data,
      colorEffect: state.colorEffect ?? 'NONE',
      pixelation: state.pixelation ?? 0,
      vignette: state.vignette ?? false,
      dotScreen: state.dotScreen ?? false,
      scanline: state.scanline ?? false,
      crosshatch: state.crosshatch ?? 'NONE',
      crosshatchNoise: state.crosshatchNoise ?? false,
      crosshatchParchment: state.crosshatchParchment ?? false,
    });
  }, [state, data, svgRendererVal, needToLoadGPUTier]);

  if (selector) {
    return selector(renderState);
  }
  return renderState as T;
}

export function getDefaultTextLayer(): TextLayer {
  return getLocalStorageRenderState().defaultTextLayer ?? 'DISTRICTS';
}

export function useTexturesEnabled() {
  const initialTexturesEnabled = useRenderOptions(state => state.textures);
  const gl = useThree(state => state.gl)
  return initialTexturesEnabled && gl.capabilities.isWebGL2 && gl.capabilities.maxTextures >= 16;
}

function mobileOrLowPerformance<T>(data: TierResult | null, mobile: T, notMobile: T) {
  if (!data || data?.isMobile) {
    return mobile;
  }
  return notMobile;
}

function appleOrIntelPerformance<T>(data: TierResult | null, low: T, high: T) {
  if (!data || data.gpu?.startsWith('intel') || data.gpu?.startsWith('apple')) {
    return low;
  }
  return high;
}
